import { FC, useEffect, useState } from 'react';
import { useNavigate } from '@remix-run/react';
import { Form, FormButton, FormGroup, GroupProps } from '../basic-form';
import { FormInterface } from '../basic-form/form/type/form';
import cls from 'classnames';
import formatFormData from './utils/format';
import { ReportType } from 'shared/types/report';
import { useAtomValue } from 'jotai';
import { $Locale, $UserAgent } from '@lemon8/web-app-shared/atom';
import { useFormSubmit } from '~/utils/fetch/use-submit-form';
import { useTitleVisible } from '~/utils/hooks/use-title-visible';

import 'intersection-observer';

import './style.scss';

export type ReportFormProps = {
  title: string;
  des?: string;
  reportGroupData: GroupProps[];
  reportType: ReportType;
};

export const ReportForm: FC<ReportFormProps> = (props) => {
  const { title, des, reportGroupData: reportData, reportType } = props;
  const navigate = useNavigate();
  // 控制Send按钮的loading状态
  const [isLoading, setIsLoading] = useState(false);
  const [reportGroupData, setReportGroupData] = useState(reportData);

  const { isMobile, isiOS, isInApp } = useAtomValue($UserAgent);
  const { region } = useAtomValue($Locale);

  const fetchFormSubmit = useFormSubmit();

  useTitleVisible('.report-form-title');

  const privacyPolicyHref = isInApp
    ? `sslocal://webview?url=${encodeURIComponent(`https://www.lemon8-app.com/legal/privacy?reigon=${region}`)}`
    : `https://www.lemon8-app.com/legal/privacy?reigon=${region}`;
  // form表单提交相关
  let form: FormInterface;
  const bindForm = (instance: FormInterface) => (form = instance);

  useEffect(() => {
    const formData = window.sessionStorage.getItem(`report${reportType}`);
    if (formData) {
      const _formData = JSON.parse(formData);
      setReportGroupData(
        reportData.map((group) => {
          return {
            ...group,
            formList: group.formList.map((item) => {
              return {
                ...item,
                defaultValue: _formData[item.name],
              };
            }),
          };
        }),
      );
      window.sessionStorage.removeItem(`report${reportType}`);
    }
  }, []);

  const handleSubmit = async () => {
    // 验证是否可以提交
    const result = await form.submit();
    if (!result) {
      return;
    }
    setIsLoading(true);

    const formData = form.getValues();
    const { contentURL } = formData;
    const formattedFormData = formatFormData(formData, region, reportType, isInApp);
    const finalResult = await fetchFormSubmit({
      formData: formattedFormData,
      isInApp,
      contentURL,
    });

    setIsLoading(false);

    if (!finalResult) {
      window.sessionStorage.setItem(`report${reportType}`, JSON.stringify(formData));
    }

    // 因为结果页有resend功能，所以需要history带数据到final页
    navigate('/h5/feoffline/report/final-result', {
      state: {
        result: finalResult,
        data: formattedFormData,
        contentURL,
      },
    });
  };

  return (
    <div
      className={cls('report-form', {
        pc: !isMobile,
        ios: isiOS,
      })}
    >
      <h1 className="report-form-title">{title}</h1>
      {des && <div className="report-form-des">{des}</div>}
      <div className="report-form-container">
        <Form instance={bindForm}>
          <>
            {reportGroupData.map((item, index) => {
              return (
                <div
                  className={cls('group-item', {
                    'last-item': index === reportGroupData.length - 1,
                  })}
                  key={index}
                >
                  <FormGroup {...item} />
                </div>
              );
            })}
          </>
          <FormButton isLoading={isLoading} onClick={handleSubmit} title="Send" />
        </Form>
      </div>
      <div className="report-from-footer">
        Please note that in order to process your request, Lemon8 will need to collect and store the above information.
        Any personal data you provide will be processed in accordance with our{' '}
        <a target="_blank" href={privacyPolicyHref} rel="noreferrer">
          Privacy Policy.
        </a>
      </div>
    </div>
  );
};
